import React from "react";
import { BrandFrame, Watermark } from "./shared";

// ─── Template 1: Nuovo annuncio (1080×1080) ──────────────────────
// iter 313 r21 v17 — User feedback (IMG_0710 reference + IMG_0711 bug):
// "tutti i testi sovrapposti, manca il logo, watermark sbagliato".
// Rivisto in UN SOLO LAYOUT (ispirato al composer Python già validato):
// foto piena full-bleed + banda sage gradient in basso (~32% altezza)
// con overline, titolo, indirizzo, divider, specs, prezzo, agente.
// Logo agenzia top-left + watermark Pronto bottom-right (dentro la banda
// per non sovrapporsi al footer testo).
// Gli "stili" (Elegante/Luxury/Moderno/Minimal) ora cambiano SOLO i font
// (lay.headingFont / lay.bodyFont) e il colore brand (col.accent).
export function NuovoAnnuncio({ data, lay, col, watermark }) {
  const brand = col.accent || "#7C8B6E";
  // iter 313 r21 v18 — UPPERCASE rimosso: il reference IMG_0710 mostra
  // titolo serif italic lowercase per TUTTI gli stili. Era titleUppercase
  // per Moderno/Luxury → causa il "TRILOCALE VIA SAN TOMMASO" che user
  // ha contestato. Ora uniforme.
  const title = (data.title || "").replace(/\s+·\s+/g, ", ").trim();
  const hasTitle = !!title;
  const [logoBroken, setLogoBroken] = React.useState(false);
  const overlineParts = [];
  if (data.property_type) overlineParts.push(data.property_type.toUpperCase());
  if (data.rooms) overlineParts.push(`${data.rooms} LOCALI`);
  // iter v28 r45 — Niente più fallback "IMMOBILE IN EVIDENZA": se mancano
  // i dati la riga overline non viene proprio renderizzata.
  const overline = overlineParts.join("  ·  ");
  const showLogo = data.agencyLogoUrl && !logoBroken;
  // iter v28 r441 — User M1354: fallback chain: logo → agency_name → agent_name
  const fallbackLabel = data.agencyName || data.agentName;
  const showName = !showLogo && !!fallbackLabel;

  return (
    <div
      className="relative w-full aspect-square rounded-xl overflow-hidden"
      style={{ background: "#1C1C1A", color: "#FAFAF7", boxShadow: "0 30px 60px -20px rgba(0,0,0,0.6)" }}
      data-testid="static-nuovo_annuncio"
    >
      {/* FOTO FULL-BLEED */}
      <div className="absolute inset-0" style={{ backgroundImage: `url(${data.photos.hero})`, backgroundSize: "cover", backgroundPosition: "center" }} />

      {/* GRADIENT BANDA INFERIORE — sage solid in basso, fade verso foto.
          iter v28 r438 → r439 — User M1351: «fammela sfumata ma meno trasparente,
          30% trasparenza max 40%». Ultimo stop visibile alzato a `B3` (70%
          opacità = 30% trasparenza). Resta SFUMATURA (4 stop, transizione
          dolce), ma il punto più trasparente non scende sotto il 70%.
          iter v29 r119 · User M1356: solo per Elegante e Lussuoso la barra
          deve essere meno trasparente (+10% opacità) per leggibilità testo
          serif. B3→CC (70%→80%), EC→F2 (92%→95%). */}
      {(() => {
        const isPremiumLayout = lay?.name === "Elegante" || lay?.name === "Lussuoso";
        // iter v29 r210 — User M1530: «barra Elegante/Lussuoso falla meno
        // trasparente, non si legge "in vendita"». Bumpando ulteriormente i
        // due stop alti (F2→FA = 95%→98%, CC→E6 = 80%→90%) e allungando la
        // zona piena (55%→65%) il testo serif rimane leggibile ANCHE su
        // foto chiare/bianche (es. planimetria, cucina con luce diretta).
        const stopBaseStart = isPremiumLayout ? 65 : 55;
        const stopMid = isPremiumLayout ? "FA" : "EC";
        const stopFar = isPremiumLayout ? "E6" : "B3";
        return (
          <div
            className="absolute inset-x-0 bottom-0"
            style={{
              height: "46%",
              background: `linear-gradient(to top, ${brand} 0%, ${brand} ${stopBaseStart}%, ${brand}${stopMid} 80%, ${brand}${stopFar} 94%, transparent 100%)`,
            }}
          />
        );
      })()}

      {/* TOP-LEFT — Logo agenzia (con fallback se broken) o nome italic.
          iter v28 r45 — Logo monocromatico ora diventa NERO (era bianco
          tramite invert(1)) con drop-shadow bianca multi-direzionale per
          essere leggibile anche su foto scure. Su foto chiare/bianche
          (es. planimetria) ora si legge perfettamente.
          Richiesta utente IMG_0787: "su sfondo bianco invertilo solo
          da bianco a nero". */}
      <div className="absolute top-3 left-3 z-30 pointer-events-none">
        {showLogo ? (
          <img
            // iter v28 r439 — User M1352: «il logo non si carica nei social
            // anche se nel profilo ce l'ho». Strategia di fallback robusta:
            // 1. Prima cerco la versione B/W pre-renderizzata server-side
            // 2. Se fallisce (404 o CORS), retry SENZA i bw params (raw logo)
            // 3. Se anche quello fallisce, cado sul testo (showName)
            // Questo gestisce backend pre-r437 che non hanno il pre-render
            // e ritornano 404 sul bw=1&w=2048.
            src={data.agencyLogoBwBlackUrl || data.agencyLogoUrl}
            alt={data.agencyName || ""}
            crossOrigin="anonymous"
            onError={(e) => {
              const el = e.currentTarget;
              const tries = parseInt(el.dataset.retryCount || "0", 10);
              if (tries === 0 && data.agencyLogoUrl && el.src !== data.agencyLogoUrl) {
                // Retry: caricamento raw senza bw params (per backend legacy)
                el.dataset.retryCount = "1";
                el.src = data.agencyLogoUrl;
              } else if (tries < 2) {
                // Secondo retry con cache buster per scavalcare CDN cache
                el.dataset.retryCount = "2";
                const sep = el.src.includes("?") ? "&" : "?";
                el.src = `${el.src}${sep}_r=${Date.now()}`;
              } else {
                setLogoBroken(true);
              }
            }}
            style={{
              maxWidth: 88, maxHeight: 36,
              objectFit: "contain", display: "block",
              // iter v28 r434 — logo già pre-elaborato server-side. Aggiungo
              // solo un glow bianco per leggibilità su sfondi scuri.
              filter: "drop-shadow(0 0 2px rgba(255,255,255,0.95)) drop-shadow(0 0 4px rgba(255,255,255,0.7))",
            }}
          />
        ) : showName ? (
          <div
            style={{
              color: "#FAFAF7",
              textShadow: "0 1px 3px rgba(0,0,0,0.5)",
              fontFamily: lay.accentFont || lay.bodyFont,
              fontStyle: "italic",
              fontSize: 14, lineHeight: 1.05, maxWidth: 180,
            }}
          >
            {fallbackLabel}
          </div>
        ) : null}
      </div>

      {/* TOP-RIGHT — Badge "NUOVO" piccolo */}
      <div className="absolute top-3 right-3 z-20">
        <span
          className="inline-flex items-center gap-1.5 text-[8.5px] font-bold tracking-[0.28em] uppercase rounded px-2 py-1"
          style={{ background: "#FAFAF7", color: "#1C1C1A", fontFamily: lay.bodyFont }}
        >
          <span className="w-[5px] h-[5px] rounded-full" style={{ background: "#C25450" }} />
          Nuovo
        </span>
      </div>

      {/* BANDA TESTI — overline, titolo, indirizzo, specs+prezzo, agente.
          iter v28 r45 — Spostati verso il fondo della banda colorata
          (richiesta utente IMG_0788): più aria sopra, testi compattati
          nella parte bassa. */}
      <div
        className="absolute inset-x-0 bottom-0 z-10 px-4 pt-4 pb-3 flex flex-col justify-end"
        style={{ color: "#FAFAF7", minHeight: "46%" }}
      >
        {/* Overline tick + tipologia — solo se ci sono dati */}
        {overline && (
          <div className="flex items-center gap-2 mb-1.5">
            <span className="w-3 h-[1.5px]" style={{ background: "#FAFAF7" }} />
            <span
              className="text-[8.5px] font-bold tracking-[0.24em] uppercase"
              style={{ fontFamily: lay.bodyFont, opacity: 0.92 }}
            >
              {overline}
            </span>
          </div>
        )}
        {/* Titolo — solo se presente (no placeholder "Senza annuncio") */}
        {hasTitle && (
          <h4
            style={{
              fontFamily: lay.headingFont,
              ...lay.titleStyle,
              textTransform: "none",
              fontStyle: "italic",
              fontWeight: 500,
              fontSize: 22,
              lineHeight: 1.05,
              margin: "0 0 4px 0",
              color: "#FAFAF7",
              textShadow: "0 1px 4px rgba(0,0,0,0.18)",
            }}
          >
            {title}
          </h4>
        )}
        {/* Indirizzo */}
        <div
          className="text-[10.5px] font-semibold mb-2.5"
          style={{ fontFamily: lay.bodyFont, color: "#FAFAF7", opacity: 0.93 }}
        >
          {data.address || data.city || ""}
        </div>
        {/* iter 313 r21 v18 — Divider rimosso (user "puntini sotto al titolo"). */}
        {/* Specs + Prezzo */}
        <div className="flex justify-between items-baseline gap-2">
          <div className="text-[10.5px] font-bold tracking-[0.05em]" style={{ fontFamily: lay.bodyFont }}>
            {[
              data.surface ? `${data.surface} m²` : null,
              data.rooms ? `${data.rooms} ${data.rooms === 1 ? "locale" : "locali"}` : null,
              data.bathrooms ? `${data.bathrooms} ${data.bathrooms === 1 ? "bagno" : "bagni"}` : null,
              data.floor ? `piano ${data.floor}` : null,
            ].filter(Boolean).join(" · ")}
          </div>
          <div
            style={{
              fontFamily: lay.headingFont,
              fontWeight: 700,
              fontSize: 17,
              color: "#FAFAF7",
              letterSpacing: "-0.01em",
            }}
          >
            {data.price}
          </div>
        </div>
        {/* Agente nome + numero — iOS auto-link disabilitato */}
        {(data.agentName || data.agentPhone) && (
          <div
            className="mt-2 text-[9.5px] font-semibold opacity-90"
            style={{ fontFamily: lay.bodyFont, color: "#FAFAF7" }}
          >
            {data.agentName}
            {data.agentName && data.agentPhone ? " · " : ""}
            {data.agentPhone && (
              <span style={{ textDecoration: "none", color: "inherit" }} translate="no">
                {data.agentPhone}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Watermark Pronto bottom-right (sopra alla banda, discreto) */}
      {watermark && <Watermark dark />}
    </div>
  );
}

// ─── Template 2: Prezzo aggiornato ────────────────────────────────
export function PrezzoAggiornato({ data, lay, col, watermark }) {
  const isModerno = lay.name === "Moderno";
  const isLuxury = lay.name === "Luxury";

  return (
    <div
      className="relative w-full aspect-square rounded-xl overflow-hidden grid"
      style={{ gridTemplateColumns: "0.95fr 1.05fr", background: col.bg, color: col.fg, boxShadow: "0 30px 60px -20px rgba(0,0,0,0.6)" }}
      data-testid="static-prezzo_aggiornato"
    >
      <div className="relative" style={{ backgroundImage: `url(${data.photos.hero})`, backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, transparent 55%, ${col.bg} 100%)` }} />
      </div>
      <div className={`p-6 flex flex-col ${lay.align === "center" ? "items-center text-center" : ""}`} style={{ background: col.bg }}>
        <span
          className={`inline-flex items-center gap-1.5 text-[9.5px] font-bold tracking-[0.22em] uppercase rounded-full mb-3 px-2.5 py-1.5 ${lay.align === "center" ? "self-center" : "self-start"}`}
          style={{ background: col.accent, color: col.isDark ? col.bg : "#FFF", fontFamily: lay.bodyFont }}
        >
          <span style={{ fontSize: 11, fontWeight: 800 }}>↓</span>
          {isModerno ? "RIBASSO" : "Nuovo prezzo"}
        </span>
        <div style={{ fontFamily: lay.accentFont, fontStyle: "italic", textDecoration: "line-through", fontSize: 16, opacity: 0.4 }}>{data.previousPrice}</div>
        <div style={{ fontFamily: lay.headingFont, ...lay.titleStyle, fontSize: 36, lineHeight: 1, marginTop: 2, marginBottom: 10 }}>{data.price}</div>
        <div className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold mb-4" style={{ color: col.accent, fontFamily: lay.bodyFont }}>
          <span className="px-2 py-0.5 rounded-full font-bold tracking-wide" style={{ background: col.accent, color: col.isDark ? col.bg : "#FFF" }}>−{data.deltaPct}%</span>
          <span style={{ color: col.fg }}>Occasione</span>
        </div>
        <div style={{ fontFamily: lay.headingFont, fontSize: isLuxury ? 13 : 14, fontStyle: lay.titleStyle.fontStyle === "italic" ? "italic" : "normal" }}>{data.title}</div>
        <div className="text-[10px] mt-1" style={{ opacity: 0.6, fontFamily: lay.bodyFont }}>{data.surface} mq · {data.rooms} locali · {data.city}</div>
        <div className={`mt-auto pt-4 flex items-baseline ${lay.align === "center" ? "justify-center gap-3" : "justify-between"}`} style={{ borderTop: `1px solid ${col.border}` }}>
          <div className="text-[8.5px] font-semibold tracking-[0.2em] uppercase opacity-55" style={{ fontFamily: lay.bodyFont }}>{data.city}</div>
        </div>
      </div>
      {watermark && <BrandFrame data={data} lay={lay} theme="light" />}
    </div>
  );
}

// ─── Template 3: Venduto ──────────────────────────────────────────
export function Venduto({ data, lay, col, watermark }) {
  const isModerno = lay.name === "Moderno";
  return (
    <div
      className="relative w-full aspect-square rounded-xl overflow-hidden"
      style={{ background: col.isDark ? col.bg : "#1C1C1A", color: "#FAFAF7", boxShadow: "0 30px 60px -20px rgba(0,0,0,0.6)" }}
      data-testid="static-venduto"
    >
      <div className="absolute inset-0" style={{ backgroundImage: `url(${data.photos.hero})`, backgroundSize: "cover", backgroundPosition: "center", filter: "saturate(0.5) brightness(0.45)" }} />
      <div className="relative z-[2] h-full p-7 grid" style={{ gridTemplateRows: "auto 1fr auto" }}>
        <div className="flex justify-between items-center">
          <span
            className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] font-bold tracking-[0.34em] uppercase rounded"
            style={{
              border: `2px solid ${col.accent}`,
              color: col.accent,
              background: "rgba(0,0,0,0.18)",
              transform: isModerno ? "rotate(0deg)" : "rotate(-3deg)",
              fontFamily: isModerno ? lay.headingFont : lay.bodyFont,
            }}
          >
            <span className="w-[5px] h-[5px] rounded-full" style={{ background: col.accent }} />
            Venduto
          </span>
        </div>
        <div className={`flex flex-col justify-center items-center text-center`}>
          {isModerno ? (
            <div style={{ fontFamily: lay.headingFont, fontWeight: 900, fontSize: 70, lineHeight: 0.92, letterSpacing: "-0.03em", textTransform: "uppercase" }}>
              IN <span style={{ color: col.accent }}>{data.daysToSell}</span><br />GIORNI
            </div>
          ) : (
            <div style={{ fontFamily: lay.headingFont, fontStyle: lay.name === "Minimal" ? "normal" : "italic", fontWeight: lay.name === "Luxury" ? 200 : 400, fontSize: 64, lineHeight: 0.95, letterSpacing: "-0.02em", marginBottom: 8 }}>
              In <span style={{ color: col.accent, fontStyle: "normal", fontWeight: lay.name === "Minimal" ? 700 : 400 }}>{data.daysToSell}</span><br />giorni.
            </div>
          )}
          {/* iter 312 r4 — chiarisce di cosa parlano i "X giorni" */}
          <div className="text-[9.5px] font-semibold tracking-[0.24em] uppercase opacity-75 mt-2.5" style={{ fontFamily: lay.bodyFont }}>
            Dalla pubblicazione al rogito
          </div>
          <div className="text-[10px] font-semibold tracking-[0.28em] uppercase opacity-80 mt-1.5" style={{ fontFamily: lay.bodyFont }}>Grazie ai nostri clienti</div>
        </div>
        <div className="flex justify-center items-baseline pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.18)" }}>
          <span className="text-[9px] tracking-[0.16em] uppercase font-semibold opacity-65" style={{ fontFamily: lay.bodyFont }}>
            {new Date().toLocaleDateString("it-IT", { month: "long", year: "numeric" })}
          </span>
        </div>
      </div>
      {watermark && <BrandFrame data={data} lay={lay} theme="dark" />}
    </div>
  );
}

// ─── Template 4: Open House (Story 9:16) ─────────────────────────
export function OpenHouse({ data, lay, col, watermark }) {
  // iter v28 r195 — User M1115: rifatto. Mantiene la struttura "Open House"
  // per OGNI layout (Elegante, Luxury, Moderno, Minimal). Filtro grigio
  // scuro uniforme sopra la foto, testo centrato:
  //   • "Open House" grande (font del layout)
  //   • Giorno (es. "Domenica")
  //   • Data + ora (es. "14 giugno · Ore 17:00")
  //   • Via e città (es. "Via San Tommaso 12 · Milano")
  // No countdown, no "Prenota la visita", no "Sei invitato".
  const name = lay?.name || "Elegante";
  const isModerno = name === "Moderno";
  const isMinimal = name === "Minimal";
  const isLuxury  = name === "Luxury";
  const isElegante = name === "Elegante";

  const oh = data.openHouse || {};
  const day = oh.day || "Domenica";
  const date = oh.date || "14 giugno";
  const time = oh.time || "Ore 14:00 — 18:00";
  const address = data.address || (data.title ? data.title : "");
  const city = data.city || "";
  // Evita duplicati tipo "Via X, Milano · Milano": se la città è già
  // nell'indirizzo, non la riaggiungiamo.
  const cityAlreadyInAddress = city && address && address.toLowerCase().includes(city.toLowerCase());
  const addressLine = address && city && !cityAlreadyInAddress
    ? `${address} · ${city}`
    : (address || city);

  // Titolo "Open House" — peso/case/spacing diverso per layout
  const titleStyle = isModerno
    ? { fontFamily: lay.headingFont, fontWeight: 900, fontSize: 80, lineHeight: 0.92, letterSpacing: "-0.04em", textTransform: "uppercase" }
    : isMinimal
    ? { fontFamily: lay.headingFont, fontWeight: 300, fontSize: 64, lineHeight: 0.95, letterSpacing: "-0.04em" }
    : isLuxury
    // iter v28 r207 — Lussuoso ora Spectral ExtraLight Italic (200)
    ? { fontFamily: lay.headingFont, fontStyle: "italic", fontWeight: 200, fontSize: 82, lineHeight: 0.95, letterSpacing: "-0.01em" }
    : isElegante
    // iter v28 r207 — Elegante ora Bodoni Moda Italic (didone display)
    ? { fontFamily: lay.headingFont, fontStyle: "italic", fontWeight: 400, fontSize: 78, lineHeight: 0.94, letterSpacing: "0.01em" }
    : { fontFamily: lay.headingFont, fontStyle: "italic", fontWeight: 400, fontSize: 80, lineHeight: 0.92, letterSpacing: "-0.02em" };

  const titleText = isModerno ? "OPEN\nHOUSE" : "Open\nHouse";

  return (
    <div
      className="relative w-full aspect-[9/16] rounded-xl overflow-hidden"
      style={{
        backgroundImage: `url(${data.photos.hero})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        color: "#FAFAF7",
        boxShadow: "0 30px 60px -20px rgba(0,0,0,0.6)",
      }}
      data-testid="static-open_house"
    >
      {/* Filtro grigio scuro uniforme sopra la foto */}
      <div className="absolute inset-0" style={{ background: "rgba(28,28,26,0.55)" }} />
      {/* Vignette molto leggera per dare profondità */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at center, rgba(0,0,0,0) 55%, rgba(0,0,0,0.35) 100%)" }}
      />

      {/* Overline pill — variante per layout */}
      <div className="absolute top-6 inset-x-5 z-[2] flex justify-center">
        {isLuxury ? (
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.42em] font-semibold" style={{ fontFamily: lay.bodyFont, color: col.accent }}>
            <span className="w-6 h-px" style={{ background: col.accent }} />
            Ti aspettiamo
            <span className="w-6 h-px" style={{ background: col.accent }} />
          </div>
        ) : isModerno ? (
          <div className="inline-block px-2.5 py-1 text-[10px] uppercase tracking-[0.32em] font-bold" style={{ fontFamily: lay.bodyFont, background: "rgba(250,250,247,0.95)", color: "#1C1C1A" }}>
            Open · House
          </div>
        ) : isMinimal ? (
          <div className="text-[9px] uppercase tracking-[0.44em] font-medium opacity-85" style={{ fontFamily: lay.bodyFont }}>
            — Visita libera —
          </div>
        ) : (
          // Elegante
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.38em] font-medium" style={{ fontFamily: lay.bodyFont, color: "rgba(250,250,247,0.85)" }}>
            <span className="w-5 h-px" style={{ background: "rgba(250,250,247,0.7)" }} />
            Ti invitiamo
            <span className="w-5 h-px" style={{ background: "rgba(250,250,247,0.7)" }} />
          </div>
        )}
      </div>

      {/* Center block */}
      <div className="absolute top-1/2 -translate-y-1/2 inset-x-6 z-[2] text-center">
        <div
          style={{
            ...titleStyle,
            color: "#FAFAF7",
            whiteSpace: "pre-line",
            textShadow: "0 6px 30px rgba(0,0,0,0.4)",
          }}
        >
          {titleText}
        </div>

        {/* Decorative line (only for Elegante/Luxury) */}
        {(isElegante || isLuxury) && (
          <div className="w-[64px] h-px mx-auto my-5" style={{ background: col.accent, opacity: 0.85 }} />
        )}
        {(isModerno || isMinimal) && <div className="h-6" />}

        {/* Day */}
        <div
          className="text-[14px] uppercase tracking-[0.32em] font-semibold"
          style={{
            fontFamily: lay.bodyFont,
            color: "#FAFAF7",
            opacity: 0.92,
          }}
        >
          {day}
        </div>

        {/* Date + time line */}
        <div
          className="mt-1.5 text-[15px]"
          style={{
            fontFamily: isModerno ? lay.bodyFont : lay.headingFont,
            fontStyle: isLuxury || isElegante ? "italic" : "normal",
            fontWeight: isModerno ? 700 : 500,
            color: "#FAFAF7",
          }}
        >
          {date} · {time}
        </div>

        {/* Address */}
        {addressLine && (
          <div
            className="mt-4 text-[11.5px] uppercase tracking-[0.22em] opacity-85"
            style={{ fontFamily: lay.bodyFont, color: "rgba(250,250,247,0.92)" }}
          >
            {addressLine}
          </div>
        )}
      </div>

      {/* Bottom: agency name (subtle, no "Prenota la visita" CTA) */}
      {data.agencyName && (
        <div className="absolute bottom-6 inset-x-5 z-[2] text-center">
          <div
            className="text-[10px] uppercase tracking-[0.36em] font-semibold"
            style={{
              fontFamily: lay.bodyFont,
              color: "rgba(250,250,247,0.7)",
              borderTop: "1px solid rgba(255,255,255,0.18)",
              paddingTop: 10,
            }}
          >
            {data.agencyName}
          </div>
        </div>
      )}

      {watermark && <BrandFrame data={data} lay={lay} theme="dark" />}
    </div>
  );
}
